import { TILES, createTile } from "./dashboardTiles.js";

/**
 * Determines if spending accounts should be shown based on user's coverage.
 * @param {Object} coverage - The user's coverage data
 * @returns {boolean} True if user has any spending account benefits
 */
function getSpendingAccountActiveStatus(coverage) {
    const { health, lifestyle } = coverage.spendingAccounts;
    return health.hasBenefit === 1 || lifestyle.hasBenefit === 1;
}

/**
 * Determines if the user should see spending accounts based on their role and benefits.
 * @param {Object} userData - The user data object
 * @returns {boolean} True if spending accounts tile should be shown
 */
function shouldShowSpendingAccountTile(userData) {
    const { meta, coverage } = userData;

    // Don't show for dependents or survivor benefit parents
    if (meta.isDependent || meta.isSurvivorBenefitParent) {
        return false;
    }

    // Must have spending accounts enabled in plan design
    return getSpendingAccountActiveStatus(coverage);
}

/**
 * Returns tiles for marketplace only Members.
 * @returns {Array} Array of tile objects
 */
function getMarketplaceOnlyTiles() {
    return [
        createTile(TILES.MARKETPLACE),
        createTile(TILES.MY_ACCOUNT),
        createTile(TILES.PLAN_DETAILS)
    ];
}

/**
 * Returns tiles for Members in a SA-only class and with previous standard claims history, but is a dependent or in survivor parent status.
 * @returns {Array} Array of tile objects
 */
function getRestrictedClaimsTiles() {
    return [
        createTile(TILES.VIEW_CLAIMS),
        createTile(TILES.MY_ACCOUNT),
        createTile(TILES.PLAN_DETAILS)
    ];
}

/**
 * Returns tiles for Members with only spending accounts active and previous standard claims history
 * @returns {Array} Array of tile objects
 */
function getSpendingAccountTilesWithHistory() {
    return [
        createTile(TILES.VIEW_CLAIMS),
        createTile(TILES.SPENDING_ACCOUNTS),
        createTile(TILES.MY_ACCOUNT),
        createTile(TILES.PLAN_DETAILS)
    ];
}

/**
 * Returns tiles for Members in a spending account only class, and no previous standard claims history.
 * @returns {Array} Array of tile objects
 */
function getSpendingAccountOnlyTiles() {
    return [
        createTile(TILES.SPENDING_ACCOUNTS),
        createTile(TILES.MY_ACCOUNT),
        createTile(TILES.PLAN_DETAILS)
    ];
}

/**
 * Returns tiles for Members in a spending account only class, no previous standard claims history, but is a dependent or in survivor benefit status
 * @returns {Array} Array of tile objects
 */
function getRestrictedSpendingAccountOnlyTiles() {
    return [
        createTile(TILES.MY_ACCOUNT),
        createTile(TILES.PLAN_DETAILS)
    ];
}

/**
 * Returns standard tiles for Members without spending accounts
 * @returns {Array} Array of tile objects
 */
function getStandardTilesWithoutSA() {
    return [
        createTile(TILES.CLAIMS),
        createTile(TILES.MY_USAGE),
        createTile(TILES.COVERAGE),
        createTile(TILES.BENEFITS_CARD),
        createTile(TILES.MY_ACCOUNT)
    ];
}

/**
 * Returns standard tiles for regular Members.
 * @returns {Array} Array of tile objects
 */
function getStandardTiles() {
    return [
        createTile(TILES.CLAIMS),
        createTile(TILES.SPENDING_ACCOUNTS),
        createTile(TILES.MY_USAGE),
        createTile(TILES.COVERAGE),
        createTile(TILES.BENEFITS_CARD),
        createTile(TILES.MY_ACCOUNT)
    ];
}

/**
 * Main function to get dashboard tiles based on user context.
 * This function encapsulates all the business logic for determining which tiles
 * should be displayed to a user based on their account type, benefits, and status.
 *
 * @param {Object} userData - The user data object
 * @param {Object} userData.meta - User metadata from Redux state
 * @param {number} userData.meta.isMarketplaceOnly - Whether user is in a marketplace only account
 * @param {number} userData.meta.isSpendingAccountOnly - Whether user is in a spending account only class
 * @param {boolean} userData.meta.isDependent - Whether user is a dependent
 * @param {boolean} userData.meta.isSurvivorBenefitParent - Whether user is a survivor benefit parent
 * @param {Object} userData.coverage - User coverage data from Redux state
 * @param {Object} userData.coverage.spendingAccounts - Spending account data object
 * @param {boolean} userData.noClaims - Whether user has no claims
 * @returns {Array} Array of tile objects to display
 */
export function getDashboardTiles(userData) {
    const { meta, noClaims } = userData;
    const isMarketplaceOnly = meta.isMarketplaceOnly === 1;
    const isSpendingAccountOnly = meta.isSpendingAccountOnly === 1;
    const hasClaimsHistory = !noClaims; // rename for readability
    const canViewSpendingAccounts = shouldShowSpendingAccountTile(userData);

    // 1. Marketplace Only Members
    if (isMarketplaceOnly) {
        return getMarketplaceOnlyTiles();
    }

    // 2. Spending Account Only Logic
    if (isSpendingAccountOnly) {
        // 2A. Edge Case: Member is in a SA-Only class, has claims history, but is currently restricted (e.g., dependent, survivor benefit parent status)
        if (hasClaimsHistory && !canViewSpendingAccounts) {
            return getRestrictedClaimsTiles();
        }

        // 2B. SA-only class member with standard claims history (due to a class change from a non SA-only class)
        if (hasClaimsHistory && canViewSpendingAccounts) {
            return getSpendingAccountTilesWithHistory();
        }

        // 2C. SA-only class member with standard claims history (due to a class change from a non SA-only class)
        if (!hasClaimsHistory && !canViewSpendingAccounts) {
            return getRestrictedSpendingAccountOnlyTiles();
        }

        // 2D. Standard spending account only class member
        return getSpendingAccountOnlyTiles();
    }

    // 3. Members with no spending account access
    if (!canViewSpendingAccounts) {
        return getStandardTilesWithoutSA();
    }

    // 4. Standard Members get full tile set
    return getStandardTiles();
}